'use client'

import React from 'react'
import { Settings, Plus, RotateCcw } from 'lucide-react'
import {
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle
} from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from "@/components/ui/avatar"

// Helper to format currency (assuming it will be passed or imported)
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Define the Collaborator type based on usage in page.tsx
interface Collaborator {
  id: string;
  name: string;
  initials: string;
  imageUrl: string | null;
}

interface OverallSummaryCardProps {
  overallProgress: number;
  totalSpent: number;
  totalAllocated: number;
  remainingBudget: number;
  collaboratorData: Collaborator[];
  daysRemaining: number;
  onSettingsClick: () => void;
  onAddCollaboratorClick: () => void; // Placeholder for future functionality
  onResetPeriodClick: () => void;
}

export default function OverallSummaryCard({ 
  overallProgress, 
  totalSpent, 
  totalAllocated, 
  remainingBudget, 
  collaboratorData, 
  daysRemaining,
  onSettingsClick,
  onAddCollaboratorClick,
  onResetPeriodClick
}: OverallSummaryCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Overall Budget Summary</CardTitle>
          <CardDescription>Total spending across all "Needs" categories.</CardDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={onSettingsClick}
        >
          <Settings className="h-5 w-5" />
          <span className="sr-only">Settings</span>
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Top Group */}
        <div>
          <Progress value={overallProgress} aria-label={`${overallProgress.toFixed(0)}% spent`} />
          <div className="flex justify-between text-sm mt-2">
            <span className="font-medium">Spent: {formatCurrency(totalSpent)}</span>
            <span className="text-muted-foreground">Allocated: {formatCurrency(totalAllocated)}</span>
          </div>
          <div className="text-right mt-1">
            <Badge variant={remainingBudget >= 0 ? "secondary" : "destructive"}>
              {remainingBudget >= 0 ? `${formatCurrency(remainingBudget)} Remaining` : `${formatCurrency(Math.abs(remainingBudget))} Over Budget`}
            </Badge>
          </div>
        </div>

        {/* Bottom Group - Pushed down and aligned */}
        <div className="mt-auto pt-4 flex flex-row items-end justify-between">
          {/* Collaborators Section (Left) */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">Collaborators</h4>
            <div className="flex items-center space-x-2">
              {collaboratorData.map((collab) => (
                <Avatar key={collab.id} className="h-8 w-8 border">
                  <AvatarImage src={collab.imageUrl ?? undefined} alt={collab.name} />
                  <AvatarFallback className="text-sm">{collab.initials}</AvatarFallback>
                </Avatar>
              ))}
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full"
                onClick={onAddCollaboratorClick}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Days Remaining Display (Right) */}
          <div className="flex items-center gap-1 text-right">
            <span className="text-sm text-muted-foreground">
              {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={onResetPeriodClick}
            >
              <RotateCcw className="h-4 w-4" />
              <span className="sr-only">Reset Period</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 